// helpers related to position and size of widgets
import { WidgetsMap } from 'types/infographTypes';
import { Widget } from 'types/widget.types';
import { rotatePointAroundOrigin } from 'utils/transform';

import { WidgetRect } from './WidgetLayout.types';

/**
 * Calculates the bounding rectangle of a rotated widget.
 *
 * @param {number} x - The x-coordinate of the top-left corner of the original rectangle.
 * @param {number} y - The y-coordinate of the top-left corner of the original rectangle.
 * @param {number} width - The width of the original rectangle.
 * @param {number} height - The height of the original rectangle.
 * @param {number} rotateDeg - The rotation angle in degrees.
 * @returns An object representing the bounding rectangle of the rotated widget, with properties:
 *          leftPx (x-coordinate of the top-left corner),
 *          topPx (y-coordinate of the top-left corner),
 *          widthPx (width of the bounding rectangle),
 *          heightPx (height of the bounding rectangle).
 */
export const getBoundingRectOfRotatedWidget = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotateDeg: number,
) => {
  // Define the center of the rectangle
  const center = { xPx: x + width / 2, yPx: y + height / 2 };

  // Coordinates of the rectangle's corners before rotation
  const corners = [
    { xPx: x, yPx: y },
    { xPx: x + width, yPx: y },
    { xPx: x + width, yPx: y + height },
    { xPx: x, yPx: y + height },
  ];

  // Rotate each corner around the center
  const rotatedCorners = corners.map((corner) => rotatePointAroundOrigin(corner, center, rotateDeg));
  // Calculate the bounding rectangle
  const minX = Math.min(...rotatedCorners.map((corner) => corner.xPx));
  const minY = Math.min(...rotatedCorners.map((corner) => corner.yPx));
  const maxX = Math.max(...rotatedCorners.map((corner) => corner.xPx));
  const maxY = Math.max(...rotatedCorners.map((corner) => corner.yPx));

  return {
    leftPx: parseFloat(minX.toFixed(1)),
    topPx: parseFloat(minY.toFixed(1)),
    widthPx: parseFloat((maxX - minX).toFixed(1)),
    heightPx: parseFloat((maxY - minY).toFixed(1)),
  };
};

/**
 * Returns the WidgetRect of a widget (handles rotation).
 *
 * @param widget - The widget for which to calculate the bounding rectangle.
 * @returns The bounding rectangle of the widget.
 */
export const getWidgetRect = (widget: Widget) => {
  if (Math.floor(widget.rotateDeg) !== 0) {
    return getBoundingRectOfRotatedWidget(
      widget.leftPx,
      widget.topPx,
      widget.widthPx,
      widget.heightPx,
      widget.rotateDeg,
    );
  }

  const rect = {
    topPx: widget.topPx,
    leftPx: widget.leftPx,
    widthPx: widget.widthPx,
    heightPx: widget.heightPx,
  };

  return rect;
};

/**
 * Calculates the combined WidgetRect size from an array of WidgetRect sizes.
 *
 * @param rects - An array of widget rectangles.
 * @returns The combined rectangle that encloses all the given widget rectangles.
 */
export const getCombinedRect = (rects: WidgetRect[]): WidgetRect => {
  if (!rects || rects.length === 0) return { topPx: 0, leftPx: 0, widthPx: 0, heightPx: 0 };

  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;

  for (const rect of rects) {
    minLeft = Math.min(minLeft, rect.leftPx);
    minTop = Math.min(minTop, rect.topPx);
    maxRight = Math.max(maxRight, rect.leftPx + rect.widthPx);
    maxBottom = Math.max(maxBottom, rect.topPx + rect.heightPx);
  }

  return {
    topPx: minTop,
    leftPx: minLeft,
    widthPx: maxRight - minLeft,
    heightPx: maxBottom - minTop,
  };
};

/**
 * Check if widget A contains widget B by at least a specified percentage
 *
 * @param a - WidgetRect A
 * @param b - WidgetRect B
 * @param percent - percentage of B that must be contained within A
 */
export const isWidgetContained = (a: WidgetRect, b: WidgetRect, percent: number) => {
  const overlap = overlapPercentage(a, b);
  return overlap >= percent;
};

/**
 * Check by how many percent widget A contains widget B
 *
 * @param a - WidgetRect A
 * @param b - WidgetRect B
 * @param percent - percentage of B that must be contained within A - (e.g. 35 for 35%)
 */
export const overlapPercentage = (a: WidgetRect, b: WidgetRect) => {
  // check for 0 height and width
  if (a.widthPx === 0 || a.heightPx === 0 || b.widthPx === 0 || b.heightPx === 0) {
    return 0;
  }

  const intersectionLeft = Math.max(a.leftPx, b.leftPx);
  const intersectionRight = Math.min(a.leftPx + a.widthPx, b.leftPx + b.widthPx);
  const intersectionTop = Math.max(a.topPx, b.topPx);
  const intersectionBottom = Math.min(a.topPx + a.heightPx, b.topPx + b.heightPx);

  // No overlap
  if (intersectionRight < intersectionLeft || intersectionBottom < intersectionTop) {
    return 0;
  }

  // Check if the overlap percentage is greater than the specified threshold
  const intersectionArea = (intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop);
  const areaB = b.widthPx * b.heightPx;
  const overlapPercentage = (intersectionArea / areaB) * 100;

  return overlapPercentage;
};

/**
 * Finds the closest widget below the selected widget within a given threshold.
 *
 * @param selectedWidget - The selected widget.
 * @param pageWidgetMap - The map of widgets on the page.
 * @param threshold - The threshold value for vertical distance (default: 100).
 *
 * @returns The closest widget below the selected widget, or null if no widget is found.
 */
export const findClosestWidgetBelow = (
  selectedWidget: Widget & { widgetId: string },
  pageWidgetMap: WidgetsMap,
  threshold: number = 100,
): (Widget & { widgetId: string }) | null => {
  const { topPx, leftPx, widthPx, heightPx, widgetId } = selectedWidget;
  const bottomPx = topPx + heightPx; // Bottom edge of the selectedWidget

  let closestWidget = null;
  let closestDistance = Infinity;

  Object.entries(pageWidgetMap).forEach(([pageWidgetId, widget]) => {
    const isBelow = widget.topPx > bottomPx;
    const isWithinOrBelowThreshold = widget.topPx - bottomPx < threshold;

    if (isBelow && isWithinOrBelowThreshold && pageWidgetId !== widgetId) {
      // Ensure the box is vertically below
      // Calculate the horizontal span of both boxes to check for overlap
      const selectedBoxRightEdge = leftPx + widthPx;
      const boxRightEdge = widget.leftPx + widget.widthPx;

      // Check if there's any overlap in the horizontal plane
      const isOverlappingHorizontally =
        widget.leftPx <= selectedBoxRightEdge && // Left edge of the box is to the left of the right edge of the selectedWidget
        boxRightEdge >= leftPx; // Right edge of the box is to the right of the left edge of the selectedWidget

      if (isOverlappingHorizontally) {
        const verticalDistance = widget.topPx - bottomPx; // Vertical distance from selectedWidget to this box

        // Update closest box if this one is closer
        if (verticalDistance < closestDistance) {
          closestDistance = verticalDistance;
          closestWidget = { widgetId: pageWidgetId, ...widget };
        }
      }
    }
  });

  return closestWidget;
};

/**
 * Return the true if the widget position overlap
 *
 * @param widgetA
 * @param widgetB
 * @param tolerance - optional tolerance value for overlap - for near overlap
 * @returns {boolean}
 */
export const intersectRect = (widgetA: WidgetRect, widgetB: WidgetRect, tolerance?: number): boolean => {
  // Check overlap considering a px tolerance (optional)
  if (typeof tolerance === 'number') {
    // Round values to the nearest whole number
    const roundA = {
      leftPx: Math.round(widgetA.leftPx),
      topPx: Math.round(widgetA.topPx),
      widthPx: Math.round(widgetA.widthPx),
      heightPx: Math.round(widgetA.heightPx),
    };
    const roundB = {
      leftPx: Math.round(widgetB.leftPx),
      topPx: Math.round(widgetB.topPx),
      widthPx: Math.round(widgetB.widthPx),
      heightPx: Math.round(widgetB.heightPx),
    };

    return !(
      roundB.leftPx > roundA.leftPx + roundA.widthPx + tolerance ||
      roundB.leftPx + roundB.widthPx < roundA.leftPx - tolerance ||
      roundB.topPx > roundA.topPx + roundA.heightPx + tolerance ||
      roundB.topPx + roundB.heightPx < roundA.topPx - tolerance
    );
  }

  return !(
    widgetB.leftPx > widgetA.leftPx + widgetA.widthPx ||
    widgetB.leftPx + widgetB.widthPx < widgetA.leftPx ||
    widgetB.topPx > widgetA.topPx + widgetA.heightPx ||
    widgetB.topPx + widgetB.heightPx < widgetA.topPx
  );
};

/**
 * Check if widget A is wrapping around (contains) widget B
 * @param a - WidgetRect A
 * @param b - WidgetRect B
 */
export const isWidgetWrapping = (a: WidgetRect, b: WidgetRect) => {
  const topPxGreater = Math.round(a.topPx) <= Math.round(b.topPx);
  const widthGreater = Math.round(a.widthPx) >= Math.round(b.widthPx);
  const heightGreater = Math.round(a.heightPx) >= Math.round(b.heightPx);
  const bottomPxGreater = Math.round(a.topPx) + Math.round(a.heightPx) >= Math.round(b.topPx) + Math.round(b.heightPx);
  const rightPxGreater =
    Math.round(a.leftPx) <= Math.round(b.leftPx) &&
    Math.round(a.leftPx) + Math.round(a.widthPx) >= Math.round(b.leftPx) + Math.round(b.widthPx);

  const isWrapping = topPxGreater && widthGreater && heightGreater && bottomPxGreater && rightPxGreater;
  return isWrapping;
};
